// Super-Admin-only control to start a "View as" session: pick a person and
// (optionally) allow audited writes, then impersonate them. Only rendered for a
// Super Admin who is not already impersonating (see App.js). Selecting someone
// triggers a /me re-fetch via ViewAsContext, so the whole app re-gates.
import { useEffect, useState } from "react";
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, TextField, FormControlLabel, Checkbox, Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useViewAs } from "../ViewAsContext";

export default function ViewAsPicker() {
  const { start } = useViewAs();
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null);
  const [allowWrites, setAllowWrites] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    axios.get(`${API_BASE_URL}/personnel/`)
      .then(res => { if (active) setPeople(res.data || []); })
      .catch(() => { if (active) setPeople([]); });
    return () => { active = false; };
  }, [open]);

  const label = (p) =>
    `${p.forename || ""} ${p.surname || ""}`.trim() + (p.email ? ` (${p.email})` : "");

  const close = () => { setOpen(false); setSelected(null); setAllowWrites(false); };
  const confirm = () => {
    if (selected) start(selected.id, allowWrites);
    close();
  };

  return (
    <>
      <Button
        size="small"
        color="inherit"
        startIcon={<VisibilityIcon />}
        onClick={() => setOpen(true)}
      >
        View as
      </Button>

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>View as another person</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            See the app exactly as this person does. Read-only by default — tick
            the box to also perform actions on their behalf (recorded in the audit
            log as you acting as them).
          </Typography>
          <Autocomplete
            options={people}
            getOptionLabel={label}
            value={selected}
            onChange={(_, v) => setSelected(v)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label="Person" autoFocus margin="dense" />
            )}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={allowWrites}
                onChange={(e) => setAllowWrites(e.target.checked)}
              />
            }
            label="Allow writes (audited as you acting as them)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" onClick={confirm} disabled={!selected}>
            View as
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
